/**
 * GoogleTasksPanel — Todo Google Tasks personale dell'admin loggato.
 *
 * Mostra SOLO i task degli account Google collegati dall'admin corrente
 * (lista "@default" di ogni account). CRUD completo: crea, modifica,
 * completa/riapri, elimina. Se un account è stato collegato prima
 * dell'aggiunta dello scope Tasks, mostra il bottone "Ricollega".
 */
import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  ListTodo, Plus, Trash2, X, Pencil, RefreshCw,
  Calendar as CalIcon, Loader2, Unplug, CheckCircle2, Circle,
} from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

function formatDate(iso) {
  if (!iso) return null;
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

function todayIsoDate() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const ACCOUNT_COLORS = [
  { bg: "bg-sky-100", text: "text-sky-800", ring: "ring-sky-300" },
  { bg: "bg-amber-100", text: "text-amber-800", ring: "ring-amber-300" },
  { bg: "bg-emerald-100", text: "text-emerald-800", ring: "ring-emerald-300" },
  { bg: "bg-violet-100", text: "text-violet-800", ring: "ring-violet-300" },
];

export default function GoogleTasksPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [modalTask, setModalTask] = useState(null); // null | "new" | task object

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r = await api.get("/admin/google-tasks");
      setData(r.data);
    } catch (err) {
      if (!silent) toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Impossibile caricare Google Tasks");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh: polling ogni 60s + refresh quando la finestra torna in focus
  useEffect(() => {
    const iv = setInterval(() => load(true), 60000);
    const onFocus = () => load(true);
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(iv); window.removeEventListener("focus", onFocus); };
  }, [load]);

  const accounts = data?.accounts || [];
  const okAccounts = accounts.filter((a) => !a.needs_reconnect && !a.error && !a.api_disabled);
  const reconnectAccounts = accounts.filter((a) => a.needs_reconnect);
  const apiDisabledAccounts = accounts.filter((a) => a.api_disabled);
  const errorAccounts = accounts.filter((a) => !a.needs_reconnect && !a.api_disabled && a.error);
  const tasks = data?.tasks || [];
  const multiAccount = okAccounts.length > 1;
  const distinctLists = new Set(tasks.map((t) => t.list_title || "").filter(Boolean));
  const multiList = distinctLists.size > 1;

  const accountStyle = (email) => {
    const idx = accounts.findIndex((a) => a.google_email === email);
    return ACCOUNT_COLORS[Math.max(idx, 0) % ACCOUNT_COLORS.length];
  };

  const handleReconnect = async () => {
    try {
      const r = await api.get("/oauth/calendar/authorize");
      if (r.data?.authorization_url) window.location.href = r.data.authorization_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Errore avvio collegamento Google");
    }
  };

  const handleToggle = async (t) => {
    setBusyId(t.id);
    const newStatus = t.status === "completed" ? "needsAction" : "completed";
    try {
      await api.patch(`/admin/google-tasks/${t.id}`, { google_email: t.owner_email, list_id: t.list_id, status: newStatus });
      await load(true);
      toast.success(newStatus === "completed" ? "Task completato" : "Task riaperto");
    } catch (err) {
      handleApiError(err);
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (t) => {
    if (!window.confirm("Eliminare questo task da Google Tasks?")) return;
    setBusyId(t.id);
    try {
      await api.delete(`/admin/google-tasks/${t.id}`, { params: { google_email: t.owner_email, list_id: t.list_id } });
      await load(true);
      toast.success("Task eliminato");
    } catch (err) {
      handleApiError(err);
    } finally {
      setBusyId(null);
    }
  };

  const handleApiError = (err) => {
    const detail = err?.response?.data?.detail;
    if (detail === "GOOGLE_TASKS_SCOPE_MISSING") {
      toast.error("Permesso Google Tasks mancante: ricollega l'account Google");
    } else if (detail === "GOOGLE_TASKS_API_DISABLED") {
      toast.error("API Google Tasks non abilitata nel progetto Google Cloud");
    } else {
      toast.error(formatApiErrorDetail(detail) || "Errore Google Tasks");
    }
  };

  const pendingCount = tasks.filter((t) => t.status !== "completed").length;

  return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm" data-testid="google-tasks-panel">
      <div className="p-4 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="bg-sky-100 p-1.5 rounded-md">
            <ListTodo size={16} className="text-sky-700" />
          </div>
          <div>
            <div className="text-[10px] tracking-[0.18em] uppercase text-slate1 font-semibold">
              Todo personale
            </div>
            <h2 className="font-display text-base text-ink leading-tight">Google Tasks</h2>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => load()}
            disabled={loading}
            className="p-1.5 rounded hover:bg-slate-100 text-slate-500 transition disabled:opacity-40"
            title="Aggiorna"
            data-testid="google-tasks-refresh"
          >
            <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
          </button>
          {okAccounts.length > 0 && (
            <button
              type="button"
              onClick={() => setModalTask("new")}
              className="bg-ink hover:bg-slate-800 text-white text-[10px] tracking-wider uppercase font-semibold px-2.5 py-1.5 rounded flex items-center gap-1 transition"
              data-testid="google-tasks-new"
            >
              <Plus size={12} /> Nuovo
            </button>
          )}
        </div>
      </div>

      {/* Banner API Google Tasks non abilitata nel progetto Google Cloud */}
      {apiDisabledAccounts.length > 0 && (
        <div className="mx-4 mt-3 bg-rose-50 border border-rose-300 rounded-sm p-3" data-testid="google-tasks-api-disabled-banner">
          <div className="text-[11px] text-rose-900 leading-relaxed">
            <strong>L'API Google Tasks non è abilitata</strong> nel progetto Google Cloud usato
            per il collegamento. Per attivarla:
            <ol className="list-decimal ml-4 mt-1 space-y-0.5">
              <li>Vai su <a href="https://console.cloud.google.com/apis/library/tasks.googleapis.com" target="_blank" rel="noreferrer" className="underline font-semibold">console.cloud.google.com → Tasks API</a></li>
              <li>Seleziona il progetto usato per ARCHI-HUB (lo stesso del Calendar)</li>
              <li>Clicca <strong>Abilita</strong>, attendi 1-2 minuti e ricarica questa pagina</li>
            </ol>
          </div>
        </div>
      )}

      {/* Banner errori generici per account */}
      {errorAccounts.length > 0 && (
        <div className="mx-4 mt-3 bg-rose-50 border border-rose-200 rounded-sm p-3" data-testid="google-tasks-error-banner">
          {errorAccounts.map((a) => (
            <div key={a.google_email} className="text-[11px] text-rose-800 leading-relaxed">
              <strong>{a.google_email}</strong>: {a.error}
            </div>
          ))}
        </div>
      )}

      {/* Bottone ricollega account (scope Tasks mancante) — minimale */}
      {reconnectAccounts.length > 0 && (
        <div className="mx-4 mt-3" data-testid="google-tasks-reconnect-banner">
          <button
            type="button"
            onClick={handleReconnect}
            title={`Ricollega: ${reconnectAccounts.map((a) => a.google_email).join(", ")}`}
            className="inline-flex items-center gap-1.5 bg-amber-600 hover:bg-amber-700 text-white text-[10px] tracking-wider uppercase font-semibold px-2.5 py-1.5 rounded transition"
            data-testid="google-tasks-reconnect-btn"
          >
            <Unplug size={12} /> Ricollega account
          </button>
        </div>
      )}

      <div className="max-h-[50vh] overflow-y-auto">
        {loading && !data ? (
          <div className="p-6 text-center text-slate1 text-sm">
            <Loader2 size={16} className="inline-block animate-spin mr-2" /> Caricamento...
          </div>
        ) : !data?.connected ? (
          <div className="p-6 text-center" data-testid="google-tasks-not-connected">
            <ListTodo size={28} className="mx-auto text-slate-300 mb-2" strokeWidth={1.5} />
            <p className="text-sm text-slate1">Nessun account Google collegato</p>
            <button
              type="button"
              onClick={handleReconnect}
              className="mt-3 text-xs text-gold hover:text-amber-700 font-semibold uppercase tracking-wider"
              data-testid="google-tasks-connect"
            >
              + Collega account Google
            </button>
          </div>
        ) : tasks.length === 0 ? (
          <div className="p-6 text-center">
            <CheckCircle2 size={28} className="mx-auto text-emerald-300 mb-2" strokeWidth={1.5} />
            <p className="text-sm text-slate1">Nessun task su Google Tasks</p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {tasks.map((t) => {
              const done = t.status === "completed";
              const overdue = !done && t.due && t.due < todayIsoDate();
              const style = accountStyle(t.owner_email);
              return (
                <li
                  key={`${t.owner_email}-${t.id}`}
                  className={`p-3 hover:bg-cream/30 transition ${done ? "opacity-60" : ""}`}
                  data-testid={`google-task-${t.id}`}
                >
                  <div className="flex items-start gap-2">
                    <button
                      type="button"
                      onClick={() => handleToggle(t)}
                      disabled={busyId === t.id}
                      className="mt-0.5 text-slate-400 hover:text-emerald-600 transition disabled:opacity-40 shrink-0"
                      title={done ? "Riapri" : "Segna come fatto"}
                      data-testid={`google-task-toggle-${t.id}`}
                    >
                      {busyId === t.id ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : done ? (
                        <CheckCircle2 size={16} className="text-emerald-600" />
                      ) : (
                        <Circle size={16} />
                      )}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm text-ink font-medium leading-snug ${done ? "line-through text-slate-500" : ""}`}>
                        {t.title || "(senza titolo)"}
                      </div>
                      {t.notes && (
                        <div className="mt-0.5 text-[11px] text-slate1 leading-snug whitespace-pre-line line-clamp-2">{t.notes}</div>
                      )}
                      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[10px]">
                        {t.due && (
                          <span className={`inline-flex items-center gap-1 ${overdue ? "text-rose-700 font-semibold" : "text-slate-500"}`}>
                            <CalIcon size={10} /> {formatDate(t.due)}
                          </span>
                        )}
                        {multiList && t.list_title && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 ring-1 ring-slate-200" title={`Lista: ${t.list_title}`}>
                            {t.list_title}
                          </span>
                        )}
                        {multiAccount && (
                          <span
                            className={`inline-flex items-center px-1.5 py-0.5 rounded ring-1 ${style.bg} ${style.text} ${style.ring}`}
                            title={t.owner_email}
                          >
                            {t.owner_email.split("@")[0]}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1 items-end shrink-0">
                      <button
                        type="button"
                        onClick={() => setModalTask(t)}
                        disabled={busyId === t.id}
                        title="Modifica"
                        className="p-1.5 rounded bg-slate-50 hover:bg-sky-50 text-slate-500 hover:text-sky-700 border border-slate-200 hover:border-sky-200 transition disabled:opacity-40"
                        data-testid={`google-task-edit-${t.id}`}
                      >
                        <Pencil size={12} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(t)}
                        disabled={busyId === t.id}
                        title="Elimina"
                        className="p-1.5 rounded bg-slate-50 hover:bg-rose-50 text-slate-500 hover:text-rose-700 border border-slate-200 hover:border-rose-200 transition disabled:opacity-40"
                        data-testid={`google-task-delete-${t.id}`}
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {data?.connected && tasks.length > 0 && (
        <div className="px-4 py-2 border-t border-slate-100 text-[10px] text-slate1">
          {pendingCount} da fare · {tasks.length - pendingCount} completati
        </div>
      )}

      {modalTask && (
        <TaskModal
          task={modalTask === "new" ? null : modalTask}
          accounts={okAccounts}
          onClose={() => setModalTask(null)}
          onSaved={async () => {
            setModalTask(null);
            await load(true);
          }}
        />
      )}
    </div>
  );
}

export function TaskModal({ task, accounts, onClose, onSaved }) {
  const isEdit = Boolean(task);
  const [title, setTitle] = useState(task?.title || "");
  const [notes, setNotes] = useState(task?.notes || "");
  const [due, setDue] = useState(task?.due || "");
  const [googleEmail, setGoogleEmail] = useState(task?.owner_email || accounts?.[0]?.google_email || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (!title.trim()) {
      toast.error("Scrivi il titolo del task");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await api.patch(`/admin/google-tasks/${task.id}`, {
          google_email: task.owner_email,
          list_id: task.list_id,
          title: title.trim(),
          notes,
          due: due || null,
        });
        toast.success("Task aggiornato");
      } else {
        await api.post("/admin/google-tasks", {
          google_email: googleEmail,
          title: title.trim(),
          notes,
          due: due || null,
        });
        toast.success("Task creato su Google Tasks");
      }
      onSaved();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail === "GOOGLE_TASKS_SCOPE_MISSING"
        ? "Permesso Google Tasks mancante: ricollega l'account"
        : formatApiErrorDetail(detail) || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[200] bg-ink/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="google-task-modal"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="bg-white rounded-lg shadow-2xl w-full max-w-md p-5 animate-in slide-in-from-bottom-4"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg text-ink">{isEdit ? "Modifica task" : "Nuovo task Google"}</h3>
          <button type="button" onClick={onClose} className="p-1 hover:bg-slate-100 rounded" aria-label="Chiudi" data-testid="google-task-modal-close">
            <X size={18} />
          </button>
        </div>

        <div className="space-y-3">
          {!isEdit && accounts.length > 1 && (
            <div>
              <label htmlFor="gt-account" className="block text-[10px] tracking-[0.18em] uppercase text-slate1 font-semibold mb-1">
                Account Google
              </label>
              <select
                id="gt-account"
                value={googleEmail}
                onChange={(e) => setGoogleEmail(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:ring-2 focus:ring-gold focus:border-gold outline-none bg-white"
                data-testid="google-task-account"
              >
                {accounts.map((a) => (
                  <option key={a.google_email} value={a.google_email}>{a.google_email}</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label htmlFor="gt-title" className="block text-[10px] tracking-[0.18em] uppercase text-slate1 font-semibold mb-1">
              Titolo
            </label>
            <input
              id="gt-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={1024}
              placeholder="Es: Chiamare fornitore per conferma ordine…"
              className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:ring-2 focus:ring-gold focus:border-gold outline-none"
              data-testid="google-task-title"
              required
            />
          </div>

          <div>
            <label htmlFor="gt-notes" className="block text-[10px] tracking-[0.18em] uppercase text-slate1 font-semibold mb-1">
              Note (opzionale)
            </label>
            <textarea
              id="gt-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              maxLength={8000}
              className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:ring-2 focus:ring-gold focus:border-gold outline-none resize-none"
              data-testid="google-task-notes"
            />
          </div>

          <div>
            <label htmlFor="gt-due" className="block text-[10px] tracking-[0.18em] uppercase text-slate1 font-semibold mb-1">
              Scadenza (opzionale)
            </label>
            <input
              id="gt-due"
              type="date"
              value={due}
              onChange={(e) => setDue(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:ring-2 focus:ring-gold focus:border-gold outline-none"
              data-testid="google-task-due"
            />
          </div>
        </div>

        <div className="flex gap-2 mt-5">
          <button
            type="submit"
            disabled={saving}
            className="flex-1 bg-ink hover:bg-slate-800 text-white text-xs font-semibold uppercase tracking-wider py-2.5 rounded flex items-center justify-center gap-1.5 transition disabled:opacity-50"
            data-testid="google-task-submit"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : isEdit ? <Pencil size={14} /> : <Plus size={14} />}
            {isEdit ? "Salva modifiche" : "Crea task"}
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-5 bg-white hover:bg-slate-50 text-slate-700 border border-slate-300 text-xs font-semibold uppercase tracking-wider rounded transition disabled:opacity-50"
            data-testid="google-task-cancel"
          >
            Annulla
          </button>
        </div>
      </form>
    </div>,
    document.body,
  );
}
